// Core domain types shared between client and server.
// This is the single source of truth for game state shapes.

export type PlayerId = string;
export type TargetId = string;

export interface Vector3Like {
  x: number;
  y: number;
  z: number;
}

/** Authoritative global game state machine (server-owned). */
export enum GlobalState {
  LOBBY = "LOBBY",
  MISSION_RUNNING = "MISSION_RUNNING",
  MISSION_COMPLETE = "MISSION_COMPLETE",
  CEREMONY_PREPARING = "CEREMONY_PREPARING",
  CEREMONY_COUNTDOWN = "CEREMONY_COUNTDOWN",
  CEREMONY_PLAYING = "CEREMONY_PLAYING",
  CEREMONY_FINISHED = "CEREMONY_FINISHED",
}

export interface PlayerState {
  playerId: PlayerId;
  name: string;
  connected: boolean;
  ready: boolean;
  position: Vector3Like;
  rotationY: number;
  ping: number;
  missionComplete: boolean;
  videoReady: boolean;
  videoBufferedSeconds: number;
  targetId: TargetId | null;
  isAdmin: boolean;
}

export interface TargetState {
  targetId: TargetId;
  ownerPlayerId: PlayerId;
  label: string;
  hp: number;
  maxHp: number;
  position: Vector3Like;
  destroyed: boolean;
}

export interface MissionProgress {
  completedCount: number;
  totalCount: number;
  percent: number;
}

export interface CeremonySchedule {
  /** Server timestamp (ms, server clock) at which ceremony video should start playing. */
  ceremonyStartAt: number;
  /** Server "now" at the time this schedule was issued, for client offset sanity check. */
  issuedAt: number;
}

export interface GameConfig {
  requiredPlayers: number;
  targetHp: number;
  missionTimeLimitSeconds: number;
  ceremonyDelayMs: number;
  playerDisconnectTimeoutMs: number;
  attackCooldownMs: number;
  attackRange: number;
}

export interface PrizeConfig {
  enabled: boolean;
  /** Seconds between automatic draws while enabled. */
  intervalSeconds: number;
  /** Total number of winners to draw across the whole event. */
  totalPrizes: number;
}

export interface PrizeWinner {
  /** The winning spectator's connection-scoped id (their socket id) — the lucky draw pool is spectators only. */
  winnerId: string;
  name: string;
  wonAt: number;
}

export interface PrizeState {
  config: PrizeConfig;
  winners: PrizeWinner[];
  /** Server timestamp (ms) of the next scheduled automatic draw, or null if not running. */
  nextDrawAt: number | null;
}

export interface PublicGameSnapshot {
  state: GlobalState;
  serverTime: number;
  config: GameConfig;
  players: PlayerState[];
  targets: TargetState[];
  progress: MissionProgress;
  ceremony: CeremonySchedule | null;
  missionStartedAt: number | null;
  prize: PrizeState;
  spectatorCount: number;
}
